import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "submit", "submitIcon", "typing", "micButton", "micIcon", "voiceIndicator", "settingsPanel", "voiceSelect", "autoSpeak"]
  static values = { speakUrl: String, voice: String, submitUrl: String, voiceEnabled: Boolean }

  isSubmitting = false
  isRecording = false
  mediaRecorder = null
  audioChunks = []
  activeStream = null

  connect() {
    this.setupTextarea()
    this.focusTextarea()
    this.scrollToBottom()
    this.toggleSendMic()
    this.keepKeyboardOpen()
  }

  disconnect() {
    // CRITICAL: Release mic when leaving the page
    this.releaseAudioStream()
  }

  releaseAudioStream() {
    if (this.activeStream) {
      this.activeStream.getTracks().forEach(t => t.stop())
      this.activeStream = null
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop() } catch(e) {}
    }
    this.isRecording = false
  }

  setupTextarea() {
    if (!this.hasTextareaTarget) return
    this.textareaTarget.addEventListener('input', () => {
      this.autoResize()
      this.toggleSendMic()
    })
  }

  // Keep keyboard open by re-focusing when textarea loses focus
  keepKeyboardOpen() {
    if (!this.hasTextareaTarget) return
    this.textareaTarget.addEventListener('blur', () => {
      // Only refocus if we're not recording voice and not navigating away
      if (!this.isRecording && document.visibilityState === 'visible') {
        // Small delay to allow intentional taps (links, buttons) to register first
        this._refocusTimer = setTimeout(() => {
          if (document.activeElement?.tagName !== 'SELECT' && 
              document.activeElement?.tagName !== 'INPUT' &&
              !document.querySelector('.axle-settings-open')) {
            this.textareaTarget.focus()
          }
        }, 100)
      }
    })
  }

  focusTextarea() {
    if (!this.hasTextareaTarget) return
    setTimeout(() => this.textareaTarget.focus(), 100)
  }

  autoResize() {
    if (!this.hasTextareaTarget) return
    const ta = this.textareaTarget
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'
  }

  toggleSendMic() {
    const hasText = this.hasTextareaTarget && this.textareaTarget.value.trim().length > 0
    if (this.hasSubmitTarget) this.submitTarget.style.display = hasText ? 'flex' : 'none'
    if (this.hasMicButtonTarget) this.micButtonTarget.style.display = !hasText && this.voiceEnabledValue ? 'flex' : 'none'
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.handleSubmit()
    }
  }

  submit(event) {
    event.preventDefault()
    this.handleSubmit()
  }

  // ═══ Suggestion Chips ═══
  insertSuggestion(event) {
    const text = event.currentTarget.textContent.trim()
    if (this.hasTextareaTarget) {
      this.textareaTarget.value = text
      this.toggleSendMic()
      this.handleSubmit()
    }
  }

  // ═══ Text Submit ═══
  async handleSubmit() {
    if (this.isSubmitting) return
    const message = this.hasTextareaTarget ? this.textareaTarget.value.trim() : ''
    if (!message) return

    this.isSubmitting = true
    this.addUserMessage(message)
    this.textareaTarget.value = ''
    this.autoResize()
    this.toggleSendMic()
    this.showTyping()
    this.scrollToBottom()

    // Notify Axle mascot
    document.dispatchEvent(new CustomEvent("axle:state", { detail: { state: "thinking" } }))

    try {
      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        body: JSON.stringify({ message }),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => null)
        throw new Error(errData?.error || `Error ${response.status}`)
      }

      const data = await response.json()
      this.hideTyping()

      if (data.success) {
        document.dispatchEvent(new CustomEvent("axle:state", { detail: { state: "success", duration: 2000 } }))
        const assistantMsg = data.messages?.find(m => m.role === 'assistant')
        if (assistantMsg) {
          this.addAssistantMessage(assistantMsg)
          // Auto-speak if enabled
          if (this.shouldAutoSpeak() && assistantMsg.content) {
            this.speakText(assistantMsg.content)
          }
        }
        // Show org cards if included
        if (data.organizations?.html?.length > 0) {
          this.addOrganizationCards(data.organizations)
        }
      } else {
        this.addErrorMessage(data.error || 'Something went wrong.')
      }
    } catch (error) {
      this.hideTyping()
      document.dispatchEvent(new CustomEvent("axle:state", { detail: { state: "warning", duration: 3000 } }))
      this.addErrorMessage(error.message || 'Failed to send message.')
    } finally {
      this.isSubmitting = false
      this.refocusInput()
    }
  }

  // ═══ Voice Recording ═══
  async startVoice(event) {
    event.preventDefault()
    if (!this.voiceEnabledValue) return
    if (this.isRecording) return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.activeStream = stream
      this.mediaRecorder = new MediaRecorder(stream)
      this.audioChunks = []

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data)
      }

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        this.processVoiceInput()
      }

      this.mediaRecorder.start()
      this.isRecording = true
      if (this.hasMicButtonTarget) this.micButtonTarget.classList.add('recording')
      if (this.hasVoiceIndicatorTarget) this.voiceIndicatorTarget.style.display = 'flex'
    } catch (err) {
      console.error('Mic access denied:', err)
    }
  }

  stopVoice(event) {
    event.preventDefault()
    if (!this.isRecording || !this.mediaRecorder) return

    this.mediaRecorder.stop()
    this.isRecording = false
    if (this.hasMicButtonTarget) this.micButtonTarget.classList.remove('recording')
    if (this.hasVoiceIndicatorTarget) this.voiceIndicatorTarget.style.display = 'none'
  }

  async processVoiceInput() {
    if (this.audioChunks.length === 0) return

    const blob = new Blob(this.audioChunks, { type: 'audio/webm' })
    const formData = new FormData()
    formData.append('audio', blob, 'recording.webm')

    this.showTyping()

    try {
      // Use browser's SpeechRecognition as fallback, or send audio to backend
      // For now, use Web Speech API for transcription
      const text = await this.transcribeWithWebSpeech()
      if (text) {
        this.textareaTarget.value = text
        this.handleSubmit()
      } else {
        this.hideTyping()
      }
    } catch (err) {
      this.hideTyping()
      console.error('Voice processing error:', err)
    }
  }

  transcribeWithWebSpeech() {
    return new Promise((resolve) => {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        resolve(null)
        return
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognition = new SpeechRecognition()
      recognition.lang = 'en-US'
      recognition.continuous = false
      recognition.interimResults = false

      recognition.onresult = (event) => {
        const transcript = event.results[0]?.[0]?.transcript
        resolve(transcript || null)
      }
      recognition.onerror = () => resolve(null)
      recognition.onend = () => {} // handled by onresult/onerror

      recognition.start()

      // Auto-stop after 10s
      setTimeout(() => {
        try { recognition.stop() } catch(e) {}
      }, 10000)
    })
  }

  // ═══ Text-to-Speech ═══
  async speakText(text) {
    if (!this.voiceEnabledValue) return
    if (!this.speakUrlValue) return
    // Strip markdown/html for clean speech
    const cleanText = text.replace(/[#*_`\[\]()]/g, '').replace(/<[^>]*>/g, '').trim()
    if (!cleanText) return

    try {
      const response = await fetch(this.speakUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'audio/mpeg, application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ text: cleanText, voice: this.voiceValue || 'CYw3kZ02Hs0563khs1Fj' })
      })

      const contentType = response.headers.get('Content-Type') || ''
      if (response.ok && contentType.startsWith('audio/')) {
        const audioBlob = await response.blob()
        const audioUrl = URL.createObjectURL(audioBlob)
        const audio = new Audio(audioUrl)
        audio.play()
      } else if (!response.ok) {
        await response.json().catch(() => null)
      }
    } catch (err) {
      console.error('TTS error:', err)
    }
  }

  shouldAutoSpeak() {
    if (!this.voiceEnabledValue) return false
    if (this.hasAutoSpeakTarget) return this.autoSpeakTarget.checked
    return false
  }

  // ═══ UI Helpers ═══
  addUserMessage(text) {
    const container = document.getElementById('messages-container')
    if (!container) return
    // Remove empty state
    const empty = container.querySelector('.axle-chat-empty')
    if (empty) empty.remove()

    const div = document.createElement('div')
    div.className = 'axle-msg axle-msg-user'
    div.innerHTML = `<div class="axle-msg-bubble">${this.escapeHtml(text)}</div>`
    // Insert before typing indicator
    const typing = container.querySelector('.axle-typing')
    if (typing) container.insertBefore(div, typing)
    else container.appendChild(div)
  }

  addAssistantMessage(msg) {
    const container = document.getElementById('messages-container')
    if (!container) return

    const div = document.createElement('div')
    div.className = 'axle-msg axle-msg-axle'
    div.innerHTML = `
      <div class="axle-msg-avi"><img src="${this.axleAvatarSrc()}" class="axle-msg-avi-img"></div>
      <div class="axle-msg-bubble"><div class="prose prose-sm dark:prose-invert max-w-none">${msg.formatted_content || this.escapeHtml(msg.content)}</div></div>
    `
    const typing = container.querySelector('.axle-typing')
    if (typing) container.insertBefore(div, typing)
    else container.appendChild(div)
    this.scrollToBottom()
  }

  addOrganizationCards(orgs) {
    const container = document.getElementById('messages-container')
    if (!container) return
    orgs.html.forEach(html => {
      const div = document.createElement('div')
      div.className = 'axle-msg axle-msg-axle'
      div.innerHTML = `<div class="axle-msg-avi"><img src="${this.axleAvatarSrc()}" class="axle-msg-avi-img"></div><div class="axle-msg-bubble">${html}</div>`
      const typing = container.querySelector('.axle-typing')
      if (typing) container.insertBefore(div, typing)
      else container.appendChild(div)
    })
    this.scrollToBottom()
  }

  addErrorMessage(text) {
    const container = document.getElementById('messages-container')
    if (!container) return
    const div = document.createElement('div')
    div.className = 'axle-msg axle-msg-axle'
    div.innerHTML = `
      <div class="axle-msg-avi"><img src="${this.axleAvatarSrc()}" class="axle-msg-avi-img"></div>
      <div class="axle-msg-bubble" style="border-color:rgba(239,68,68,0.2);color:rgba(252,165,165,0.9)">${this.escapeHtml(text)}</div>
    `
    const typing = container.querySelector('.axle-typing')
    if (typing) container.insertBefore(div, typing)
    else container.appendChild(div)
    this.scrollToBottom()
  }

  showTyping() {
    // Try Stimulus target first, then DOM fallback
    const el = this.hasTypingTarget ? this.typingTarget : document.querySelector('.axle-typing')
    if (el) {
      el.style.display = 'flex'
      el.style.visibility = 'visible'
    }
    this.scrollToBottom()
  }

  hideTyping() {
    const el = this.hasTypingTarget ? this.typingTarget : document.querySelector('.axle-typing')
    if (el) {
      el.style.display = 'none'
    }
  }

  scrollToBottom() {
    const container = document.getElementById('messages-container')
    if (container) {
      requestAnimationFrame(() => { container.scrollTop = container.scrollHeight })
    }
  }

  refocusInput() {
    if (!this.hasTextareaTarget) return
    const ta = this.textareaTarget
    // iOS drops focus on DOM change — hammer it back
    const refocus = () => {
      ta.focus()
      ta.click() // iOS sometimes needs a click to trigger keyboard
    }
    refocus()
    setTimeout(refocus, 50)
    setTimeout(refocus, 150)
    setTimeout(refocus, 300)
    setTimeout(refocus, 600)
  }

  axleAvatarSrc() {
    const img = document.querySelector('.axle-chat-avatar')
    return img ? img.src : ''
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // ═══ Voice Settings ═══
  toggleVoiceSettings() {
    if (this.hasSettingsPanelTarget) {
      this.settingsPanelTarget.classList.toggle('axle-settings-open')
    }
  }

  onVoiceChange() {
    if (this.hasVoiceSelectTarget) {
      this.voiceValue = this.voiceSelectTarget.value
    }
  }
}
